import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Slider} from 'primeng/slider';
import {TranslocoPipe} from '@jsverse/transloco';
import {toSignal} from '@angular/core/rxjs-interop';
import {ImportanceScale, PairwiseComparisonRequest} from '@frontend/shared-api';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {EnumTranslationMapperService} from '../../../../../../../shared/services/enum-translation-mapper.service';
import {PairwiseComparisonX, PairwiseItemX} from '../../evaluation-criteria-x.types';
import {PairwiseRowX} from './pairwise-block-x.types';

const ORDERED_SCALES: ImportanceScale[] = [
  ImportanceScale.PARAMOUNT_IMPORTANCE,
  ImportanceScale.VERY_VERY_STRONG_IMPORTANCE,
  ImportanceScale.VERY_STRONG_OR_PROVEN_IMPORTANCE,
  ImportanceScale.STRONG_TO_VERY_STRONG_IMPORTANCE,
  ImportanceScale.STRONG_IMPORTANCE,
  ImportanceScale.MODERATE_IMPORTANCE,
  ImportanceScale.A_LITTLE_MORE_IMPORTANCE,
  ImportanceScale.LIGHT_OR_WEAK_IMPORTANCE,
  ImportanceScale.EQUAL_IMPORTANCE,
  ImportanceScale.SLIGHTLY_LESS_IMPORTANCE,
  ImportanceScale.MODERATELY_LESS_IMPORTANCE,
  ImportanceScale.MUCH_LESS_IMPORTANCE,
  ImportanceScale.STRONG_TO_VERY_STRONG_LESS_IMPORTANCE,
  ImportanceScale.VERY_MUCH_LESS_IMPORTANCE,
  ImportanceScale.EXTREMELY_LESS_IMPORTANCE,
  ImportanceScale.LEAST_IMPORTANCE,
  ImportanceScale.ABSOLUTE_UNIMPORTANCE,
];

const EQUAL_INDEX = ORDERED_SCALES.indexOf(ImportanceScale.EQUAL_IMPORTANCE);

export const SLIDER_MIN = -EQUAL_INDEX;
export const SLIDER_MAX = ORDERED_SCALES.length - 1 - EQUAL_INDEX;

export function scaleToSliderValue(scale: ImportanceScale): number {
  return ORDERED_SCALES.indexOf(scale) - EQUAL_INDEX;
}

export function sliderValueToScale(sliderValue: number): ImportanceScale {
  return ORDERED_SCALES[EQUAL_INDEX + sliderValue];
}

export function sliderValueToLabelScale(sliderValue: number): ImportanceScale {
  return ORDERED_SCALES[EQUAL_INDEX - Math.abs(sliderValue)];
}

@Component({
  selector: 'app-pairwise-block-x',
  imports: [
    FormsModule,
    Slider,
    TranslocoPipe,
    TranslateDirectusPipe,
    ProjectStepSaveButtonComponent,
  ],
  templateUrl: './pairwise-block-x.component.html',
  styles: [
    `
      :host ::ng-deep .p-slider {
        --p-slider-track-height: 0.75rem;
        --p-slider-track-background: transparent;
        background: transparent;
      }
      :host ::ng-deep .p-slider-range {
        display: none;
      }
      :host ::ng-deep .p-slider-handle {
        background: #fff;
        border-color: #fff;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PairwiseBlockXComponent {
  private readonly enumTranslation = inject(EnumTranslationMapperService);

  readonly translationKeyPrefix = input.required<string>();
  readonly identifierPrefix = input.required<string>();
  readonly testId = input.required<string>();
  readonly items = input.required<PairwiseItemX[]>();
  readonly comparisons = input.required<PairwiseComparisonX[]>();
  readonly disabled = input(false);
  readonly saving = input(false);

  readonly save = output<PairwiseComparisonRequest[]>();

  private readonly draft = signal<Map<number, ImportanceScale>>(new Map());

  protected readonly sliderMin = SLIDER_MIN;
  protected readonly sliderMax = SLIDER_MAX;

  protected readonly scaleOptions = toSignal(this.enumTranslation.getImportanceScaleOptions(), {
    initialValue: [],
  });

  protected readonly scaleHintKey = computed<string>(
    () => `${this.translationKeyPrefix()}.pairwise.scaleHint`,
  );

  private readonly sortedItems = computed<PairwiseItemX[]>(() =>
    [...this.items()].sort((first, second) => first.sortNumber - second.sortNumber),
  );

  protected readonly baseItem = computed<PairwiseItemX | undefined>(() => this.sortedItems()[0]);

  private readonly scaleLabelByEnum = computed<Map<ImportanceScale, string>>(
    () => new Map(this.scaleOptions().map((option) => [option.enum, option.translation])),
  );

  private readonly savedScaleById = computed<Map<number, ImportanceScale>>(() => {
    const base = this.baseItem();
    if (!base) return new Map<number, ImportanceScale>();

    return new Map(
      this.comparisons()
        .filter((comparison) => comparison.firstId === base.id || comparison.secondId === base.id)
        .map((comparison) => [
          comparison.firstId === base.id ? comparison.secondId : comparison.firstId,
          comparison.importanceScale,
        ]),
    );
  });

  protected readonly rows = computed<PairwiseRowX[]>(() => {
    const savedScaleById = this.savedScaleById();
    const draft = this.draft();
    const scaleLabelByEnum = this.scaleLabelByEnum();

    return this.sortedItems()
      .slice(1)
      .map((item) => {
        const value = draft.get(item.id) ?? savedScaleById.get(item.id) ?? null;
        const sliderValue = scaleToSliderValue(value ?? ImportanceScale.EQUAL_IMPORTANCE);
        const fillShare = (Math.abs(sliderValue) / SLIDER_MAX) * 50;

        return {
          id: item.id,
          identifier: item.identifier,
          translations: item.translations,
          value,
          sliderValue,
          scaleLabel: scaleLabelByEnum.get(sliderValueToLabelScale(sliderValue)) ?? '',
          leftIsStronger: sliderValue < 0,
          rightIsStronger: sliderValue > 0,
          fillLeft: sliderValue < 0 ? 50 - fillShare : 50,
          fillWidth: fillShare,
        };
      });
  });

  protected readonly showsBlock = computed<boolean>(() => this.rows().length > 0);

  protected readonly showsSaveButton = computed<boolean>(
    () => this.draft().size > 0 && !this.disabled(),
  );

  constructor() {
    effect(() => {
      this.comparisons();
      untracked(() => this.draft.set(new Map()));
    });
  }

  onSliderChange(itemId: number, sliderValue: number): void {
    const draft = new Map(this.draft());
    draft.set(itemId, sliderValueToScale(sliderValue));
    this.draft.set(draft);
  }

  onSave(): void {
    const base = this.baseItem();
    if (!base) return;

    this.save.emit(
      this.rows()
        .filter((row) => row.value !== null)
        .map((row) => ({
          firstId: base.id,
          secondId: row.id,
          importanceScale: row.value as ImportanceScale,
        })),
    );
  }
}
